import { useLazyQuery } from '@apollo/client';
import {
  Icon,
  IconButton,
  IDropdownOption,
  Stack,
  Text,
  TooltipHost,
  useTheme,
} from '@fluentui/react';
import { useEffect, useState } from 'react';
import {
  Control,
  FieldArrayWithId,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { BusinessRuleSelections, BusinessRuleSelectionsVariables } from './__generated__/BusinessRuleSelections';

import { EditRuleModal } from './EditRuleModal';

import { useCommonStyles } from 'common/styles';
import { DEFAULT_FIELD_NAMES } from '../constant';
import RenderField from '../RenderField';
import { EditRuleViewValues } from '../types';
import BUSINESS_RULES_SELECTION from './BusinessRuleSelections.graphql';


interface EditRuleViewProps {
  control: Control<EditRuleViewValues>;
  index: number;
  field: FieldArrayWithId<EditRuleViewValues, 'rules', 'id'>;
  remove: (index: number) => void;
  businessRuleCategoriesOptions: IDropdownOption<any>[];
}

export const EditRuleView: React.FC<EditRuleViewProps> = ({
  field,
  businessRuleCategoriesOptions,
  index,
  remove,
}) => {
  const commonStyles = useCommonStyles();
  const theme = useTheme();
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const { control } = useFormContext();
  const [fetchBusinessRuleSelection, { data: businessRuleSelectionData }] =
    useLazyQuery<BusinessRuleSelections, BusinessRuleSelectionsVariables>(
      BUSINESS_RULES_SELECTION,
      {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-only',
      }
    );
 const businessRuleSelectionOptions =
   businessRuleSelectionData?.businessRuleSelections?.nodes.map(
     (item) =>
       ({
         key: item.selectionValue,
         text: item.selectionLabel,
       } as IDropdownOption)
   ) || [];

  useEffect(() => {
    if (field.layoutType === 'BR_DROP_DOWN') {
      fetchBusinessRuleSelection({
        variables: {
          orderBy: [
            'CONFIGURATION_CATEGORY_ID_ASC',
            'BUSINESS_RULE_TYPE_ID_ASC',
            'SELECTION_LABEL_ASC',
          ],
          filter: {
            businessRuleTypeId: { equalTo: field.businessRuleTypeId },
            configurationCategoryId: {
              equalTo: field.configurationCategoryId,
            },
          },
        },
      });
    }
  }, [field.layoutType]);

  const ruleValue = useWatch({
    control,
    name: `rules.${index}`,
  });

  return (
    <Stack
      horizontal
      tokens={{ childrenGap: 70 }}
      style={{
        verticalAlign: 'center',
      }}
    >
      <Stack style={{ justifyContent: 'center' }}>
        <Text
          className={commonStyles.colorThemePrimary}
          style={{
            textDecoration: 'none',
            width: 400,
            fontWeight:'bold'
          }}
        >
          {ruleValue.name}
        </Text>
      </Stack>
      {field.layoutType && (
        <Stack style={{ width: 300 }}>
          <TooltipHost content={field.columnHint ?? ''}>
            <RenderField
              layoutType={field.layoutType}
              name={`rules.${index}.${DEFAULT_FIELD_NAMES[field.layoutType]}`}
              businessRuleSelectionOptions={businessRuleSelectionOptions}
              isUpdatable={!!field?._isUpdatable}
            />
          </TooltipHost>
        </Stack>
      )}

      <Stack horizontal style={{ marginTop: 5, width: 100, marginLeft: 110 }}>
        {field._isConfigEditingAllowed && (
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            onClick={() => setEditModalOpen(true)}
          />
        )}
        {field._isDeletable && (
          <IconButton
            iconProps={{ iconName: 'Delete' }}
            onClick={() => {
              remove(index);
            }}
          />
        )}
        {field.warningMessage && (
          <Stack style={{ marginLeft: 100, justifyContent: 'center' }}>
            <TooltipHost content={field.warningMessage}>
              <Icon
                iconName="Warning"
                style={{ color: theme.palette.redDark }}
              />
            </TooltipHost>
          </Stack>
        )}
      </Stack>
      {editModalOpen && (
        <EditRuleModal
          onClose={() => {
            setEditModalOpen(false);
          }}
          index={index}
          businessRuleCategoriesOptions={businessRuleCategoriesOptions}
          categoryOptionDisabled={!field.isCategorySwitchingAllowed}
        />
      )}
    </Stack>
  );
};
